
export interface FileNavigationState {
  pathStack: Array<{ id: string; type: 'folder' | 'file' }>;
  folderId: string;
  parentFolderId?: string;
  fileId?: string;
  isViewingFile: boolean;
  view?: 'grid' | 'table';
}

/**
 * Parses the URL pathname to extract navigation state
 * URL formats:
 * - /files (root)
 * - /files/{folderId} (folder view)
 * - /files/{parentId}/{folderId}/{fileId}?type=file (file in nested folder)
 * - /files/{folderId}/{fileId}?type=file (file in root folder)
 * 
 * When type=file is in the URL, IDs are assigned by position:
 * - 2 segments total: segments[1] = FolderID = FileID (same folder is being viewed as file)
 * - 3 segments total: segments[1] = FolderID, segments[2] = FileID
 * - 4 segments total: segments[1] = ParentID, segments[2] = FolderID, segments[3] = FileID
 */
export const parseFileNavigationPath = (pathname: string, search: string = ''): FileNavigationState | null => {
  const segments = pathname.split('/').filter(Boolean);
  const params = new URLSearchParams(search);
  
  if (segments[0] !== 'files') return null;

  if (segments.length < 2) {
    // Root files path, no folder selected
    return {
      pathStack: [],
      folderId: '',
      parentFolderId: params.get('parent') || undefined,
      isViewingFile: false
    };
  }

  const firstSegment = segments[1];

  // Special case: root file path (/files/-/{fileId})
  if (firstSegment === '-' && segments[2]) {
    return {
      pathStack: [{ id: segments[2], type: 'file' }],
      folderId: '',
      fileId: segments[2],
      parentFolderId: params.get('parent') || undefined,
      isViewingFile: true
    };
  }

  let pathStack: Array<{ id: string; type: 'folder' | 'file' }> = [];
  let folderId = '';
  let parentFolderId: string | undefined = undefined;
  let fileId: string | undefined = undefined;
  let isViewingFile = false;

  // Check if viewing a file - type=file in query params is the authoritative indicator
  const isFile = params.get('type') === 'file' || params.has('fileId');

  if (isFile) {
    // File view - parse based on number of segments
    if (segments.length === 2) {
      // /files/{fileId} - file at root level (no folder)
      fileId = segments[1];
      folderId = '';  // Root-level file has no folder
      pathStack = [{ id: fileId, type: 'file' }];
      isViewingFile = true;
    } else if (segments.length === 3) {
      // /files/{folderId}/{fileId}
      folderId = segments[1];
      fileId = segments[2];
      pathStack = [
        { id: folderId, type: 'folder' },
        { id: fileId, type: 'file' }
      ];
      isViewingFile = true;
    } else if (segments.length >= 4) {
      // /files/{parentId}/{folderId}/{fileId}
      parentFolderId = segments[1];
      folderId = segments[2];
      fileId = segments[3];
      pathStack = [
        { id: parentFolderId, type: 'folder' },
        { id: folderId, type: 'folder' },
        { id: fileId, type: 'file' }
      ];
      isViewingFile = true;
    }
  } else {
    // Folder view - default behavior
    if (segments.length === 2) {
      // Single folder: /files/{folderId}
      folderId = segments[1];
      pathStack = [{ id: folderId, type: 'folder' }];
    } else if (segments.length === 3) {
      // Nested folder: /files/{parentId}/{folderId}
      parentFolderId = segments[1];
      folderId = segments[2];
      pathStack = [
        { id: parentFolderId, type: 'folder' },
        { id: folderId, type: 'folder' }
      ];
    } else if (segments.length > 3) {
      // Multiple nested folders: /files/{...}/{...}/{folderId}
      folderId = segments[segments.length - 1];
      
      for (let i = 1; i < segments.length; i++) {
        pathStack.push({ id: segments[i], type: 'folder' });
      }
      
      // Parent is the folder before current
      if (pathStack.length > 1) {
        parentFolderId = pathStack[pathStack.length - 2].id;
      }
    }
  }

  return {
    pathStack,
    folderId,
    parentFolderId,
    fileId,
    isViewingFile,
    view: (params.get('view') as 'grid' | 'table') || undefined
  };
};

/**
 * Builds a URL for the file explorer with folder and optional file ID
 * Automatically adds type=file query param when fileId is provided
 */
export const buildFileNavigationPath = (
  folderId: string | undefined,
  parentFolderId?: string,
  fileId?: string,
  view?: 'grid' | 'table'
): string => {
  let path = '/files';
  let queryParts: string[] = [];

  // Build path: /files/{parentId}/{folderId}/{fileId}
  if (parentFolderId) {
    path += `/${parentFolderId}`;
  }

  if (folderId) {
    path += `/${folderId}`;
  }

  // Add file if viewing file
  if (fileId) {
    // For root-level files (no folder), use /files/-/{fileId} pattern
    // so the URL parser doesn't confuse the fileId with a folderId
    if (!folderId && !parentFolderId) {
      path += `/-`;
    }
    path += `/${fileId}`;
    queryParts.push('type=file');
  }

  // Add view mode
  if (view) {
    queryParts.push(`view=${view}`);
  }

  const query = queryParts.length > 0 ? `?${queryParts.join('&')}` : '';
  return path + query;
};

/**
 * Gets the view mode from URL search params
 */
export const getViewModeFromUrl = (search: string): 'grid' | 'table' => {
  const params = new URLSearchParams(search);
  const view = params.get('view');
  return view === 'grid' ? 'grid' : 'table';
};

/**
 * Removes the file ID from the current path (navigates back to folder only)
 */
export const removeFileFromPath = (pathname: string, search: string): string => {
  const state = parseFileNavigationPath(pathname, search);
  if (!state) return '/files';

  // Return folder path without file
  return buildFileNavigationPath(state.folderId || '', state.parentFolderId, undefined, state.view);
};

/**
 * Adds or updates the file ID in the path, preserving parent context
 */
export const updateFileInPath = (
  pathname: string,
  search: string,
  fileId: string
): string => {
  const state = parseFileNavigationPath(pathname, search);
  if (!state) return '/files';

  // Build path with parent -> folder -> file hierarchy
  return buildFileNavigationPath(state.folderId || '', state.parentFolderId, fileId, state.view);
};
